/**
 * DashboardMenu — header "Dashboards ▾" dropdown.
 *
 * The popover is rendered via a React portal at document.body with
 * position: fixed so it escapes any backdrop-filter / overflow stacking
 * contexts created by the surrounding header and dashboard chrome.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardEntry, dashboardForPath } from '../../config/dashboards';
import { useVisibleDashboards } from '../../hooks/useVisibleDashboards';
import { useViewport } from '../../hooks/useViewport';

interface DashboardMenuProps {
  /** When true, render as a flat full-width list instead of a popover trigger. */
  inline?: boolean;
  /** Called after the user navigates to a dashboard — useful for closing a parent drawer. */
  onNavigate?: () => void;
}

const DashboardMenu: React.FC<DashboardMenuProps> = ({ inline = false, onNavigate }) => {
  const { loading, dashboards: entries } = useVisibleDashboards();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useViewport();
  const [open, setOpen] = useState(false);
  const [triggerHover, setTriggerHover] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const activeId = dashboardForPath(location.pathname)?.id;
  const isActive = (entry: DashboardEntry) => entry.id === activeId;

  const updateCoords = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({
      top: rect.bottom + 6,
      left: rect.right,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateCoords();
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        popoverRef.current && !popoverRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (loading) return null;
  if (entries.length <= 1 && !inline) {
    return null;
  }

  const handleSelect = (entry: DashboardEntry) => {
    navigate(entry.route);
    setOpen(false);
    onNavigate?.();
  };

  if (inline) {
    return (
      <div style={styles.inlineList}>
        {entries.map((entry) => {
          const active = isActive(entry);
          const hover = hoveredId === entry.id;
          return (
            <button
              type="button"
              key={entry.id}
              onClick={() => handleSelect(entry)}
              onMouseEnter={() => setHoveredId(entry.id)}
              onMouseLeave={() => setHoveredId((id) => (id === entry.id ? null : id))}
              style={{
                ...styles.inlineItem,
                ...(active ? styles.inlineItemActive : {}),
                ...(hover && !active ? styles.inlineItemHover : {}),
              }}
              aria-current={active ? 'page' : undefined}
            >
              <span style={styles.icon} aria-hidden="true">{entry.icon}</span>
              <span style={styles.itemLabel}>{entry.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  const popover = open && coords ? ReactDOM.createPortal(
    <div
      ref={popoverRef}
      role="menu"
      style={{
        ...styles.popover,
        ...(isMobile
          ? { ...styles.popoverMobile, top: coords.top, left: 16, right: 16 }
          : { top: coords.top, left: Math.max(16, coords.left - 320) }
        ),
      }}
    >
      {entries.map((entry) => {
        const active = isActive(entry);
        const hover = hoveredId === entry.id;
        return (
          <button
            type="button"
            key={entry.id}
            role="menuitem"
            onClick={() => handleSelect(entry)}
            onMouseEnter={() => setHoveredId(entry.id)}
            onMouseLeave={() => setHoveredId((id) => (id === entry.id ? null : id))}
            style={{
              ...styles.popoverItem,
              ...(active ? styles.popoverItemActive : {}),
              ...(hover && !active ? styles.popoverItemHover : {}),
            }}
          >
            <span style={styles.icon} aria-hidden="true">{entry.icon}</span>
            <div style={styles.popoverItemBody}>
              <span style={styles.itemLabel}>{entry.label}</span>
              <span style={styles.itemDescription}>{entry.description}</span>
            </div>
          </button>
        );
      })}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setTriggerHover(true)}
        onMouseLeave={() => setTriggerHover(false)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          ...styles.trigger,
          ...(triggerHover && !open ? styles.triggerHover : {}),
          ...(open ? styles.triggerOpen : {}),
        }}
      >
        Dashboards <span style={styles.caret} aria-hidden="true">▾</span>
      </button>
      {popover}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  trigger: {
    backgroundColor: 'transparent',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-2)',
    padding: '0.5rem var(--space-md)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: 500,
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
    minHeight: '40px',
    fontFamily: 'var(--font-sans)',
  },
  triggerHover: {
    backgroundColor: 'var(--accent-soft)',
    color: 'var(--accent-soft-fg)',
    borderColor: 'var(--accent)',
  },
  triggerOpen: {
    backgroundColor: 'var(--accent)',
    color: '#fff',
    borderColor: 'var(--accent)',
  },
  caret: { marginLeft: '0.25rem', fontSize: '0.625rem' },
  popover: {
    position: 'fixed',
    minWidth: 280,
    maxWidth: 320,
    background: '#fff',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    padding: 6,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    // Above the header/drawer tiers but below modals — a raw value here once
    // out-stacked the help modal after the modal moved onto --z-modal.
    zIndex: 'calc(var(--z-modal) - 1)' as React.CSSProperties['zIndex'],
    fontFamily: 'var(--font-sans)',
    animation: 'fadeInUp 180ms var(--ease-standard)',
  },
  popoverMobile: {
    maxWidth: 'unset',
  },
  popoverItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-md)',
    padding: '10px 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    fontSize: '0.875rem',
    textAlign: 'left',
    minHeight: '44px',
  },
  popoverItemActive: {
    background: 'var(--accent-soft)',
    color: 'var(--accent-soft-fg)',
    fontWeight: 600,
  },
  popoverItemHover: {
    background: 'var(--cg-neutral-50)',
  },
  popoverItemBody: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  itemLabel: { fontWeight: 600, fontSize: '0.875rem' },
  itemDescription: { color: 'var(--fg-3)', fontSize: '0.75rem' },
  icon: { fontSize: '1.25rem', flexShrink: 0 },
  inlineList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  inlineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-md)',
    padding: '12px 14px',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '48px',
    textAlign: 'left',
  },
  inlineItemActive: {
    background: 'var(--accent)',
    borderColor: 'var(--accent)',
    color: '#fff',
  },
  inlineItemHover: {
    background: 'var(--accent-soft)',
    borderColor: 'var(--border-2)',
  },
};

export default DashboardMenu;
